/**
 * Shared WebSocket connection lifecycle for channel-scoped live features
 * (queue updates, chat feed). Handles connect + reconnect-on-close like
 * before, plus recovery from a connection that's silently dead: API
 * Gateway's WS idle timeout, a laptop sleep/wake, or a network change (e.g.
 * mobile wifi→cellular) can all leave `readyState` stuck at OPEN with no
 * more frames ever arriving, so `onclose`/`onerror` alone never fire and the
 * tab quietly stops receiving broadcasts while still showing "live" (issue
 * #24). We recover two ways:
 *  - a periodic ping/pong heartbeat — if a pong (or any other frame) doesn't
 *    arrive shortly after a ping, the connection is treated as dead and
 *    replaced immediately rather than waiting on a close event that may
 *    never come.
 *  - forcing a fresh connection whenever the tab regains visibility or the
 *    browser regains network connectivity, since those are exactly the
 *    moments a zombie connection tends to have accumulated.
 */
import { useEffect, useRef, useState } from "react";
import { config } from "../lib/config";

const RECONNECT_MS = 2000;
const HEARTBEAT_MS = 4 * 60 * 1000; // well under API Gateway's 10-min idle timeout
const STALE_MS = 20 * 1000; // no frame since the last ping ⇒ assume dead

export function useChannelSocket(channelId: string, onMessage: (msg: unknown) => void) {
  const [live, setLive] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let heartbeatTimer: ReturnType<typeof setInterval> | undefined;
    let staleTimer: ReturnType<typeof setTimeout> | undefined;

    const teardown = (ws: WebSocket) => {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        ws.close();
      } catch {
        /* already closed/closing */
      }
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (staleTimer) clearTimeout(staleTimer);
    };

    // Ends whatever connection is live (if any) and opens a fresh one right
    // away, instead of waiting on `onclose` — which a zombie connection may
    // never fire.
    const restart = () => {
      if (cancelled) return;
      if (retryTimer) clearTimeout(retryTimer);
      if (socketRef.current) teardown(socketRef.current);
      connect();
    };

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(
        `${config.wsUrl}?channelId=${encodeURIComponent(channelId)}`,
      );
      socketRef.current = ws;

      const armStaleTimer = () => {
        if (staleTimer) clearTimeout(staleTimer);
        staleTimer = setTimeout(restart, STALE_MS);
      };

      ws.onopen = () => {
        if (cancelled) return;
        setLive(true);
        heartbeatTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
          armStaleTimer();
        }, HEARTBEAT_MS);
      };
      ws.onmessage = (ev) => {
        if (staleTimer) clearTimeout(staleTimer);
        try {
          onMessageRef.current(JSON.parse(String(ev.data)));
        } catch {
          /* ignore non-JSON frames */
        }
      };
      ws.onclose = () => {
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        if (staleTimer) clearTimeout(staleTimer);
        if (cancelled) return;
        setLive(false);
        retryTimer = setTimeout(connect, RECONNECT_MS);
      };
      // `error` is always followed by `close`, which owns the reconnect.
      ws.onerror = () => ws.close();
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") restart();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", restart);

    connect();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", restart);
      if (retryTimer) clearTimeout(retryTimer);
      if (socketRef.current) teardown(socketRef.current);
    };
  }, [channelId]);

  return { live };
}
